import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import { colors, spacing, typography } from '../theme';

// Badge base styles
export const badgeStyles = StyleSheet.create({
	badge: {
		borderRadius: 5,
		paddingHorizontal: spacing.s,
		paddingVertical: spacing.xs,
		marginRight: spacing.s,
		marginBottom: spacing.s,
		height: 'auto',
		width: 'auto',
	},
	badgeText: {
		color: 'white',
		fontWeight: typography.fontWeight.bold as any,
		fontSize: typography.fontSize.small,
	},
	primary: {
		backgroundColor: colors.primary,
	},
});

// Status badge color helpers
export const getStatusStyle = (
	status: string,
): {
	backgroundColor: string;
	color?: string;
} => {
	switch (status.toLowerCase()) {
		case 'quote':
			return {
				backgroundColor: colors.successLight,
				color: colors.textDark,
			};
		case 'open':
			return {
				backgroundColor: colors.info,
			};
		case 'in-progress':
		case 'in_progress':
			return {
				backgroundColor: colors.warning,
			};
		case 'completed':
			return { backgroundColor: colors.success };
		case 'on-hold':
		case 'on_hold':
			return { backgroundColor: colors.purple };
		case 'canceled':
		case 'cancelled':
			return { backgroundColor: colors.error };
		case 'pending_materials':
		case 'pending_vendor':
			return { backgroundColor: '#00897b' };
		default:
			return { backgroundColor: colors.textLight };
	}
};

// Priority badge color helpers
export const getPriorityStyle = (
	priority: string | number,
	mode: 'normal' | 'subtle' | 'outline' = 'normal',
): StyleProp<ViewStyle> & {
	backgroundColor: string;
	color?: string;
} => {
	// Handle numeric priorities (legacy)
	if (typeof priority === 'number' || !isNaN(Number(priority))) {
		const numPriority = Number(priority);
		switch(numPriority) {
			case 0:
				if (mode === 'outline') {
					return {
						backgroundColor: 'transparent',
						borderWidth: 2,
						borderColor: colors.high,
					};
				}
				return {
					backgroundColor: mode === 'subtle' ? colors.criticalLight : colors.critical,
					color: mode === 'subtle' ? colors.textDark : colors.textLight,
				};
			case 1:
				if (mode === 'outline') {
					return {
						backgroundColor: 'transparent',
						borderWidth: 2,
						borderColor: colors.high,
					};
				}
				return {
					backgroundColor: mode === 'subtle' ? colors.highLight : colors.high,
					color: mode === 'subtle' ? colors.textDark : colors.textLight,
				};
			case 2:
				if (mode === 'outline') {
					return {
						backgroundColor: 'transparent',
						borderWidth: 2,
						borderColor: colors.medium,
					};
				}
				return {
					backgroundColor: mode === 'subtle' ? colors.mediumLight : colors.medium,
					color: mode === 'subtle' ? colors.textDark : colors.textLight,
				};
			case 3:
				if (mode === 'outline') {
					return {
						backgroundColor: 'transparent',
						borderWidth: 2,
						borderColor: colors.low,
					};
				}
				return {
					backgroundColor: mode === 'subtle' ? colors.lowLight : colors.low,
					color: mode === 'subtle' ? colors.textDark : colors.textLight,
				};
			case 4:
				if (mode === 'outline') {
					return {
						backgroundColor: 'transparent',
						borderWidth: 2,
						borderColor: colors.critical,
					};
				}
				return {
					backgroundColor: mode === 'subtle' ? colors.criticalLight : colors.critical,
					color: mode === 'subtle' ? colors.textDark : colors.textLight,
				};
			default:
				if (mode === 'outline') {
					return {
						backgroundColor: 'transparent',
						borderWidth: 2,
						borderColor: colors.critical,
					};
				}
				return { backgroundColor: mode === 'subtle' ? colors.criticalLight : colors.critical };
		}
	}

	// Handle string priorities
	switch (priority.toString().toLowerCase()) {
		case 'low':
			return getPriorityStyle(4, mode);
		case 'medium':
			return getPriorityStyle(3, mode);
		case 'high':
			return getPriorityStyle(2, mode);
		case 'critical':
		case 'urgent':
			return getPriorityStyle(0, mode);
		default:
			return getPriorityStyle(5, mode);
	}
};
